import React, { useState, useEffect } from 'react';
import { Users, X, Copy, Check, Play, MessageSquare, Sparkles, LogIn, Radio } from 'lucide-react';
import { api, type MediaItem } from '../lib/api';
import { useThemeLanguage } from '../context/ThemeLanguageContext';

export interface WatchPartyModalProps {
  isOpen: boolean;
  onClose: () => void;
  mediaItem?: MediaItem | null;
  onStartPlayback?: (roomCode: string, isHost: boolean) => void;
  user?: any;
}

export function WatchPartyModal({
  isOpen,
  onClose,
  mediaItem,
  onStartPlayback,
  user,
}: WatchPartyModalProps) {
  const { language, t, showToast } = useThemeLanguage();
  const [tab, setTab] = useState<'create' | 'join'>('create');
  const [roomCode, setRoomCode] = useState('');
  const [joinInputCode, setJoinInputCode] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [copied, setCopied] = useState(false);
  const [roomState, setRoomState] = useState<any>(null);

  if (!isOpen) return null;

  const handleCreateRoom = async () => {
    if (!mediaItem) return;
    setIsCreating(true);
    try {
      const res = await api.createWatchParty({
        hostId: user?.id || 'guest',
        hostName: user?.displayName || user?.username || (language === 'he' ? 'צופה אקטיבי' : 'Homeflix Viewer'),
        mediaItemId: mediaItem.id,
        title: language === 'he' && mediaItem.titleHe ? mediaItem.titleHe : mediaItem.title,
      });
      if (res?.data?.code || res?.code) {
        const code = res?.data?.code || res?.code;
        setRoomCode(code);
        setRoomState(res?.data || res);
        showToast(language === 'he' ? 'חדר הצפייה נוצר בהצלחה!' : 'Watch Party room created!', 'success');
      }
    } catch {
      showToast(language === 'he' ? 'שגיאה ביצירת החדר' : 'Failed to create room', 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const handleJoinRoom = async () => {
    const clean = joinInputCode.trim().toUpperCase();
    if (!clean) return;
    setIsJoining(true);
    try {
      const res = await api.joinWatchParty(clean, {
        userId: user?.id || 'guest',
        name: user?.displayName || user?.username || (language === 'he' ? 'אורח' : 'Guest'),
      });
      if (res?.data || res?.code) {
        setRoomCode(clean);
        setRoomState(res?.data || res);
        showToast(language === 'he' ? 'הצטרפת לחדר בהצלחה!' : 'Joined room successfully!', 'success');
        if (onStartPlayback) {
          onStartPlayback(clean, false);
          onClose();
        }
      }
    } catch {
      showToast(language === 'he' ? 'קוד חדר לא תקין או שהחדר נסגר' : 'Invalid room code or room expired', 'error');
    } finally {
      setIsJoining(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(roomCode);
    setCopied(true);
    showToast(language === 'he' ? 'קוד החדר הועתק ללוח!' : 'Room code copied to clipboard!', 'success');
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
      <div className="relative w-full max-w-md rounded-2xl bg-neutral-900/95 border border-white/10 shadow-2xl p-6 text-white overflow-hidden">
        {/* Glowing Ambient Corner Accent */}
        <div className="absolute -top-16 -right-16 w-36 h-36 bg-red-600/20 rounded-full blur-3xl pointer-events-none" />

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10 text-neutral-400 hover:text-white transition-all cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#E50914]/20 border border-[#E50914]/30 text-[#E50914]">
            <Radio className="w-5 h-5 animate-pulse" />
          </div>
          <div>
            <h2 className="text-xl font-black">
              {language === 'he' ? 'צפייה משותפת (Watch Party)' : 'Watch Party'}
            </h2>
            <p className="text-xs text-neutral-400">
              {language === 'he' ? 'סנכרון וידאו מלא בזמן אמת עם חברים' : 'Real-time synchronized playback with friends'}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex rounded-xl bg-black/50 p-1 mb-6 border border-white/5">
          <button
            onClick={() => setTab('create')}
            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              tab === 'create'
                ? 'bg-[#E50914] text-white shadow-lg'
                : 'text-neutral-400 hover:text-white'
            }`}
          >
            {language === 'he' ? 'יצירת חדר חדש' : 'Create Room'}
          </button>
          <button
            onClick={() => setTab('join')}
            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              tab === 'join'
                ? 'bg-[#E50914] text-white shadow-lg'
                : 'text-neutral-400 hover:text-white'
            }`}
          >
            {language === 'he' ? 'הצטרפות לחדר' : 'Join Room'}
          </button>
        </div>

        {tab === 'create' ? (
          <div className="space-y-4">
            {mediaItem && (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                <img
                  src={mediaItem.posterPath || mediaItem.backdropPath || ''}
                  alt={mediaItem.title}
                  className="w-12 h-16 object-cover rounded-lg shrink-0"
                />
                <div className="min-w-0">
                  <h4 className="text-sm font-bold truncate">
                    {language === 'he' && mediaItem.titleHe ? mediaItem.titleHe : mediaItem.title}
                  </h4>
                  <span className="text-[10px] text-neutral-400">
                    {mediaItem.year ? `${mediaItem.year} • ` : ''}
                    {mediaItem.type === 'movie' ? (language === 'he' ? 'סרט' : 'Movie') : (language === 'he' ? 'סדרה' : 'Series')}
                  </span>
                </div>
              </div>
            )}

            {!roomCode ? (
              <button
                onClick={handleCreateRoom}
                disabled={isCreating}
                className="w-full py-3 rounded-xl bg-[#E50914] hover:bg-[#b80710] active:scale-98 text-white font-bold text-sm shadow-xl shadow-red-600/30 transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                {isCreating ? (language === 'he' ? 'יוצר חדר...' : 'Creating...') : (language === 'he' ? 'פתח חדר צפייה עכשיו' : 'Launch Watch Party')}
              </button>
            ) : (
              <div className="space-y-4 animate-in fade-in">
                <div className="p-4 rounded-xl bg-emerald-950/40 border border-emerald-500/30 text-center">
                  <span className="text-[11px] font-semibold text-emerald-400 block mb-1">
                    {language === 'he' ? 'קוד החדר שלך מוכן' : 'Your Room Code Is Ready'}
                  </span>
                  <div className="flex items-center justify-center gap-3 mt-2">
                    <span className="text-3xl font-black tracking-widest text-white font-mono bg-black/50 px-4 py-1.5 rounded-lg border border-white/20">
                      {roomCode}
                    </span>
                    <button
                      onClick={handleCopyLink}
                      className="p-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all cursor-pointer"
                      title={language === 'he' ? 'העתק קוד' : 'Copy Code'}
                    >
                      {copied ? <Check className="w-5 h-5 text-emerald-400" /> : <Copy className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <button
                  onClick={() => {
                    if (onStartPlayback) {
                      onStartPlayback(roomCode, true);
                      onClose();
                    }
                  }}
                  className="w-full py-3 rounded-xl bg-white hover:bg-neutral-200 text-black font-extrabold text-sm shadow-xl transition-all cursor-pointer flex items-center justify-center gap-2 active:scale-98"
                >
                  <Play className="w-4 h-4 fill-current" />
                  {language === 'he' ? 'התחל לצפות (כמנהל החדר)' : 'Start Synchronized Playback'}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-neutral-300 block mb-1.5">
                {language === 'he' ? 'הזן קוד חדר שקיבלת מחבר:' : 'Enter room code from your friend:'}
              </label>
              <input
                type="text"
                placeholder="e.g. WP-4821"
                value={joinInputCode}
                onChange={(e) => setJoinInputCode(e.target.value.toUpperCase())}
                className="w-full px-4 py-3 rounded-xl bg-black/60 border border-white/15 text-white font-mono tracking-wider focus:outline-none focus:border-[#E50914] text-center text-lg uppercase"
              />
            </div>

            <button
              onClick={handleJoinRoom}
              disabled={isJoining || !joinInputCode.trim()}
              className="w-full py-3 rounded-xl bg-[#E50914] hover:bg-[#b80710] active:scale-98 text-white font-bold text-sm shadow-xl shadow-red-600/30 transition-all cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <LogIn className="w-4 h-4" />
              {isJoining ? (language === 'he' ? 'מתחבר...' : 'Joining...') : (language === 'he' ? 'הצטרף לצפייה' : 'Join Watch Party')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
